// The payload a correlation-finder partial draws, ported from the coffee partial's
// renderPartialModel. The pair itself is resolved next door (`partial-data.js`) -- what the two
// patterns address and what they are called, read off the survey and report registries -- and
// arrives as `partialData`; everything derived from the pair and its answer is worked out here.

// A pattern addressing a question that counts respondents carries a margin of error worth
// drawing. Anything else -- an open end, a number, a tag -- does not, so the bounds come off.
const COUNTABLE_QUESTION_TYPES = ['rb', 'cb', 'mx', 'sr', 'se', 'mp', 'ro', 'aro', 'sh', 'sft', 'mn']

const isUncountable = (source, type) => 'question' === source && !COUNTABLE_QUESTION_TYPES.includes(type)

const dropErrorBounds = answer => {
  for (const batchKey of Object.keys(answer)) {
    if (!Object.hasOwn(answer[batchKey], 'total')) continue

    for (const cellKey of Object.keys(answer[batchKey])) {
      if (!Object.hasOwn(answer[batchKey][cellKey], 'amount')) continue

      delete answer[batchKey][cellKey].errLO
      delete answer[batchKey][cellKey].errHI
    }
  }
}

// A schema arrives beside the answer when the pair was answered with headers of its own. It names
// the axis the chart draws and what the table lists, and the two fall back to each other.
const resolveSchemaHeaders = (schema, ignorePercents) => {
  schema.headers.chart = schema.headers.chart || (schema.headers.table && [schema.headers.table[0]]) || null
  schema.headers.table = schema.headers.table || schema.headers.chart || null

  if (null == schema.headers.chart) return

  schema.answerType = schema.headers.chart[0]
  // the payload carries an axis-title key from here on, whether or not the schema named one
  if (!Object.hasOwn(schema, 'yAxisTitle')) schema.yAxisTitle = undefined
  schema.ignoreAmount = false
  schema.ignorePercents = 0 > schema.headers.chart.indexOf('percents') || false

  if (schema.format) schema.format.amountOnly = ignorePercents
}

// A one-sample error is answered per confidence level rather than as a pair of bounds, so the
// bounds the chart reads are filled from it, the same value on each side.
const fillErrorBounds = (answer, schema, confidenceLevels) => {
  const answerType = schema.answerType || 'amount'

  for (const batchKey of Object.keys(answer)) {
    for (const cellKey of Object.keys(answer[batchKey])) {
      const cell = answer[batchKey][cellKey]
      if (!cell?.errorMeanOneSample) continue

      cell.errLO = cell.errLO || {}
      cell.errLO[answerType] = cell.errLO[answerType] || Array.from({ length: confidenceLevels.length }, () => 0)
      cell.errHI = cell.errHI || {}
      cell.errHI[answerType] = cell.errHI[answerType] || Array.from({ length: confidenceLevels.length }, () => 0)

      confidenceLevels.forEach((confidenceLevel, index) => {
        const value = cell.errorMeanOneSample[Math.round(1000 * confidenceLevel[0])]
        const bound = Number.isNaN(Number(value)) ? value[answerType] : value

        cell.errLO[answerType][index] = bound
        cell.errHI[answerType][index] = bound
      })
    }
  }
}

export const calcModelData = ({ model, partialData, confidenceLevels = [] }) => {
  const answer = structuredClone(model.answer || {})
  const headers = model.headers

  if (
    isUncountable(partialData.source[0], partialData.type[0]) ||
    isUncountable(partialData.source[1], partialData.type[1])
  ) {
    dropErrorBounds(answer)
  }

  const data = {
    id: model.id,
    key: model.key,
    type: model.variant,
    text: partialData.text,
    batches: partialData.batches,
    answer,
    qType: 'md' === partialData.type[1] ? 'md' : '',
    mode: model.mode,
    headers,
    ignoreRType: true,
    ignoreLogic: true,
    ignoreTotal: partialData.ignoreTotal,
    ignorePercents: 0 > headers?.chart?.indexOf('percents'),
    confidenceLevel: model.confidenceLevel,
    significanceLevel: model.significanceLevel,
    legendEnabled: 'md' !== partialData.type[1]
  }

  if (!Object.hasOwn(answer, 'schema')) return data

  const schema = answer.schema
  delete answer.schema

  if (schema.headers) {
    resolveSchemaHeaders(schema, data.ignorePercents)

    if (null != schema.headers.error) fillErrorBounds(answer, schema, confidenceLevels)
  }

  return { ...data, ...schema }
}
